import { BottomSheetScrollView } from '@gorhom/bottom-sheet'
import { type ReactNode, useState } from 'react'
import { Pressable, StyleSheet, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { InfoTooltip } from '../components/InfoTooltip'
import { TopBar } from '../components/TopBar'
import {
  FigmaChevronDownIcon,
  FigmaChevronRightIcon,
} from '../icons/FigmaSystemIcons'
import { TokenChainIcon } from '../icons/TokenChainIcon'
import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'
import type { AvailableChain } from '../transfer/availableChains'
import { formatDepositAddress } from '../transfer/formatDepositAddress'

interface Props {
  targetAmount: string
  tokenAmount?: string
  amountError?: boolean
  amountLoading?: boolean
  onRetryAmount?: () => void
  tokenSymbol: string
  depositAddress?: string
  selectedChain?: AvailableChain
  onCopy: (value: string, label?: string) => void
  onBack: () => void
  onClose: () => void
  onChangeChain: () => void
}

/** Figma's one-time-address layout; the selected asset and network determine the send amount. */
export function LockedTransferCryptoScreen({
  targetAmount,
  tokenAmount,
  amountError = false,
  amountLoading = false,
  onRetryAmount,
  tokenSymbol,
  depositAddress,
  selectedChain,
  onCopy,
  onBack,
  onClose,
  onChangeChain,
}: Props) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const { height } = useWindowDimensions()
  const [addressExpanded, setAddressExpanded] = useState(false)

  let addressValue = '—'
  if (depositAddress) {
    addressValue = addressExpanded
      ? depositAddress
      : formatDepositAddress(depositAddress, 6, 4)
  }

  return (
    <Box
      style={{
        height,
        paddingTop: insets.top,
        backgroundColor: theme.background.modal,
      }}
    >
      <TopBar
        title={t('lockedTransfer.title')}
        onBack={onBack}
        onClose={onClose}
      />
      <BottomSheetScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          { paddingBottom: insets.bottom + 24 },
        ]}
      >
        <Box
          flex={1}
          alignItems="center"
          justifyContent="center"
          style={styles.amountRegion}
        >
          <Text
            variant="bodyMedium"
            numberOfLines={1}
            adjustsFontSizeToFit
            style={styles.targetAmount}
          >
            {targetAmount}
          </Text>
        </Box>

        <Box style={styles.rows}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('selectAsset.title')}
            onPress={onChangeChain}
            style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
          >
            <Box
              flexDirection="row"
              alignItems="center"
              style={[
                styles.row,
                { backgroundColor: theme.background.secondary },
              ]}
            >
              <TokenChainIcon
                symbol={tokenSymbol}
                chainIconSrc={selectedChain?.iconUri}
                size={32}
              />
              <Box flex={1}>
                <Text variant="detail">{t('lockedTransfer.send')}</Text>
                <Text variant="body">
                  {tokenSymbol && selectedChain
                    ? t('lockedTransfer.tokenOnChain', {
                        token: tokenSymbol,
                        chain: selectedChain.name,
                      })
                    : t('selectAsset.title')}
                </Text>
              </Box>
              <FigmaChevronRightIcon size={20} />
            </Box>
          </Pressable>
          <AmountRow
            amount={tokenAmount}
            tokenSymbol={tokenSymbol}
            isLoading={amountLoading}
            error={amountError}
            onCopy={onCopy}
            onRetry={onRetryAmount}
            labelAccessory={
              <InfoTooltip
                title={t('lockedTransfer.amountInfoTitle')}
                description={t('lockedTransfer.amountInfoDescription', {
                  amount: targetAmount,
                })}
              />
            }
          />
          <CopyRow
            label={t('lockedTransfer.address')}
            value={addressValue}
            copyLabel={t('common.copyAddress')}
            disabled={!depositAddress}
            onCopy={() => {
              if (depositAddress) {
                void onCopy(depositAddress, t('common.addressCopied'))
              }
            }}
            disclosureLabel={
              depositAddress && !addressExpanded
                ? t('lockedTransfer.showFullAddress')
                : undefined
            }
            onExpand={() => setAddressExpanded(true)}
          />
        </Box>
      </BottomSheetScrollView>
    </Box>
  )
}

function AmountRow({
  amount,
  tokenSymbol,
  isLoading = false,
  error = false,
  onCopy,
  onRetry,
  labelAccessory,
}: {
  amount?: string
  tokenSymbol: string
  isLoading?: boolean
  error?: boolean
  onCopy: (value: string, label?: string) => void
  onRetry?: () => void
  labelAccessory?: ReactNode
}) {
  const { t } = useFunkitTranslation()
  const isRetry = error && !!onRetry
  const isDisabled = isLoading || (error ? !isRetry : !amount)

  let value = amount ? `${amount} ${tokenSymbol}` : '—'
  if (isLoading) {
    value = t('lockedTransfer.amountLoading')
  } else if (error) {
    value = t('lockedTransfer.amountUnavailable')
  }

  return (
    <CopyRow
      label={t('lockedTransfer.amount')}
      labelAccessory={labelAccessory}
      value={value}
      error={error && !isLoading}
      copyLabel={
        isRetry ? t('common.tryAgain') : t('lockedTransfer.copyAmount')
      }
      actionLabel={isRetry ? t('common.tryAgain') : undefined}
      disabled={isDisabled}
      onCopy={() => {
        if (isRetry) {
          onRetry?.()
        } else if (amount) {
          void onCopy(amount, t('lockedTransfer.amountCopied'))
        }
      }}
    />
  )
}

function CopyRow({
  label,
  labelAccessory,
  value,
  copyLabel,
  disabled,
  onCopy,
  actionLabel,
  disclosureLabel,
  onExpand,
  error = false,
}: {
  label: string
  labelAccessory?: ReactNode
  value: string
  copyLabel: string
  actionLabel?: string
  disclosureLabel?: string
  onExpand?: () => void
  error?: boolean
  disabled: boolean
  onCopy: () => void
}) {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  return (
    <Box
      flexDirection="row"
      alignItems="center"
      style={[styles.row, { backgroundColor: theme.background.secondary }]}
    >
      <Box flex={1}>
        <Box flexDirection="row" alignItems="center" style={{ gap: 6 }}>
          <Text variant="detail">{label}</Text>
          {labelAccessory}
        </Box>
        {disclosureLabel && onExpand ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={disclosureLabel}
            accessibilityState={{ expanded: false }}
            hitSlop={11}
            onPress={onExpand}
            style={styles.addressDisclosure}
          >
            <Text
              variant="body"
              numberOfLines={1}
              style={[
                styles.copyValue,
                error ? { color: theme.content.elements.error } : undefined,
              ]}
            >
              {value}
            </Text>
            <FigmaChevronDownIcon size={16} />
          </Pressable>
        ) : (
          <Text
            variant="body"
            selectable
            accessibilityLiveRegion="polite"
            style={[
              styles.copyValue,
              error ? { color: theme.content.elements.error } : undefined,
            ]}
          >
            {value}
          </Text>
        )}
      </Box>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={copyLabel}
        accessibilityState={{ disabled }}
        disabled={disabled}
        onPress={disabled ? undefined : onCopy}
        style={({ pressed }) => [
          styles.copy,
          {
            backgroundColor: pressed
              ? theme.background.button.secondary.pressed
              : theme.background.button.secondary.default,
            opacity: disabled ? theme.opacity.disabled / 100 : 1,
          },
        ]}
      >
        <Text variant="body">{actionLabel ?? t('common.copy')}</Text>
      </Pressable>
    </Box>
  )
}

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  content: { paddingHorizontal: 20, flexGrow: 1 },
  amountRegion: { minHeight: 220, paddingHorizontal: 16, paddingBottom: 48 },
  targetAmount: { fontSize: 60, lineHeight: 72, fontVariant: ['tabular-nums'] },
  copyValue: { fontVariant: ['tabular-nums'] },
  addressDisclosure: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
  },
  rows: { gap: 12 },
  row: {
    minHeight: 72,
    borderRadius: 40,
    paddingVertical: 12,
    paddingHorizontal: 20,
    gap: 12,
  },
  copy: {
    minHeight: 44,
    minWidth: 72,
    borderRadius: 24,
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
})
